import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Icons from "lucide-react";
import { logout, logoutUserSession } from "../../../features/userAuthSlice";
import { clearAddNotice } from "../../../features/cartSlice";
import { toast } from "react-toastify";
import SearchBar from "./SearchBar";
import USER_PATHS from "../USER_PATHS";
import SettingsPopup from "./SettingsPopup";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { getAvatarInitial, resolveAvatarUrl } from "../../../util/avatar";
import EmailVerifyBanner from "../../../components/EmailVerifyBanner";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.cherrfy.app";
const APP_STORE_URL = "https://apps.apple.com/us/app/cherrfy-global-shopping/id6778857186";
const APP_QR_URL = "https://raw.githubusercontent.com/hawkeye-bd/cherrfy-cdn/main/cherrfy-apk-qr.png";
const CHERRFY_LOGO_URL = "https://raw.githubusercontent.com/hawkeye-bd/cherrfy-cdn/refs/heads/main/logo/cherrfy-logo.png";
const CORPORATE_FONT_FAMILY = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

function AppleStoreLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" role="presentation" aria-hidden="true">
      <path d="M17.05 12.65c-.02-2.11 1.72-3.13 1.8-3.18-1-.47-2.03-.53-2.47-.54-1.05-.1-2.05.62-2.58.62-.54 0-1.36-.6-2.24-.58-1.15.02-2.21.67-2.8 1.7-1.2 2.07-.31 5.14.86 6.82.57.83 1.25 1.76 2.15 1.72.86-.03 1.19-.56 2.23-.56s1.34.56 2.25.54c.93-.02 1.52-.84 2.09-1.67.65-.96.92-1.89.94-1.94-.02-.01-1.79-.69-1.81-2.73Zm-1.36-4.83c.47-.58.79-1.38.7-2.18-.68.03-1.51.46-2 .99-.44.51-.83 1.33-.72 2.11.76.06 1.54-.39 2.02-.92Z" />
    </svg>
  );
}

function GooglePlayLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 28.99 31.99" className={className} role="presentation" aria-hidden="true">
      <path d="M13.54 15.28.12 29.34a3.66 3.66 0 0 0 5.33 2.16l15.1-8.6Z" fill="#ea4335" />
      <path d="m27.11 12.89-6.53-3.74-7.35 6.45 7.38 7.28 6.48-3.7a3.54 3.54 0 0 0 1.5-4.79 3.62 3.62 0 0 0-1.5-1.5z" fill="#fbbc04" />
      <path d="M.12 2.66a3.57 3.57 0 0 0-.12.92v24.84a3.57 3.57 0 0 0 .12.92L14 15.64Z" fill="#4285f4" />
      <path d="m13.64 16 6.94-6.85L5.5.51A3.73 3.73 0 0 0 3.63 0 3.64 3.64 0 0 0 .12 2.65Z" fill="#34a853" />
    </svg>
  );
}

function StoreBadgeLink({ href, label, eyebrow, eyebrowClass, name, icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className={[
        "inline-flex h-[42px] w-full items-center gap-2 rounded-[10px] border border-white/10 bg-[#0B0B0F] px-3",
        "transition-all duration-200 hover:border-white/25 hover:bg-[#1B1B21] hover:shadow-[0_6px_18px_rgba(11,11,15,0.35)] active:scale-[0.985]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B5163A] focus-visible:ring-offset-2",
      ].join(" ")}
    >
      {icon}
      <span className="flex min-w-0 flex-col items-start leading-none">
        <span className={`whitespace-nowrap text-[9px] font-medium text-white/70 ${eyebrowClass || ""}`}>{eyebrow}</span>
        <span className="mt-[2.5px] truncate text-[13.5px] font-semibold leading-none tracking-[-0.01em] whitespace-nowrap text-white">{name}</span>
      </span>
    </a>
  );
}

function AppDownloadMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);

  const openMenu = () => {
    clearTimeout(closeTimerRef.current);
    setOpen(true);
  };

  const closeMenuSoon = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 140);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (event) => {
      if (rootRef.current && rootRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearTimeout(closeTimerRef.current);
    },
    []
  );

  return (
    <div
      ref={rootRef}
      className="relative hidden xl:block"
      onMouseEnter={openMenu}
      onMouseLeave={closeMenuSoon}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex h-11 items-center gap-1.5 px-1.5 transition-colors ${open ? "text-[#B5163A]" : "text-[#20121A] hover:text-[#B5163A]"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Icons.QrCode size={22} strokeWidth={2.2} aria-hidden="true" />
        <span className="text-left leading-tight">
          <span className="block text-xs font-medium">Download</span>
          <span className="block text-xs font-medium">App</span>
        </span>
        <Icons.ChevronDown size={13} strokeWidth={2.4} aria-hidden="true" className={`mr-0.5 mt-0.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        className={[
          "absolute right-1/2 top-full z-[130] w-[min(476px,calc(100vw-2rem))] translate-x-1/2 pt-2.5",
          "origin-top transform-gpu transition-all duration-200",
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none",
        ].join(" ")}
        role="dialog"
        aria-label="Download the Cherrfy app"
        aria-hidden={!open}
        inert={open ? undefined : ""}
      >
        <div className="relative">
          <div className="absolute -top-1.5 left-1/2 z-10 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-slate-200/70 bg-white" />
          <div className="relative overflow-hidden rounded-xl border border-slate-200/70 bg-white p-4 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.22)]">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#B5163A] via-[#E0466B] to-[#B5163A]" />
            <div className="relative grid grid-cols-[104px,1fr] items-start gap-4">
            <div className="relative rounded-lg border border-slate-200/70 bg-white p-2">
              <img src={APP_QR_URL} alt="Cherrfy app QR code" className="h-[84px] w-[84px] object-contain" loading="lazy" />
              <span className="pointer-events-none absolute left-1 top-1 h-3 w-3 rounded-tl border-l-2 border-t-2 border-[#B5163A]/60" aria-hidden="true" />
              <span className="pointer-events-none absolute right-1 top-1 h-3 w-3 rounded-tr border-r-2 border-t-2 border-[#B5163A]/60" aria-hidden="true" />
              <span className="pointer-events-none absolute bottom-1 left-1 h-3 w-3 rounded-bl border-b-2 border-l-2 border-[#B5163A]/60" aria-hidden="true" />
              <span className="pointer-events-none absolute bottom-1 right-1 h-3 w-3 rounded-br border-b-2 border-r-2 border-[#B5163A]/60" aria-hidden="true" />
              <p className="mt-1 text-center text-[10px] font-medium uppercase tracking-wide text-slate-400">Scan to install</p>
            </div>
            <div className="min-w-0">
              <h2 className="text-[16px] font-semibold leading-snug tracking-[-0.01em] text-slate-950">Take Cherrfy with you</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Track orders live, unlock app-only deals, and check out in a few taps.
              </p>
              <div className="mt-3.5 grid grid-cols-2 gap-2.5">
                <StoreBadgeLink
                  href={APP_STORE_URL}
                  label="Download the Cherrfy app on the Apple App Store"
                  eyebrow="Download on the"
                  name="App Store"
                  icon={<AppleStoreLogo className="h-[19px] w-[19px] shrink-0 text-white" />}
                />
                <StoreBadgeLink
                  href={GOOGLE_PLAY_URL}
                  label="Get the Cherrfy app on Google Play"
                  eyebrow="GET IT ON"
                  eyebrowClass="uppercase tracking-[0.07em]"
                  name="Google Play"
                  icon={<GooglePlayLogo className="h-[17px] w-[17px] shrink-0" />}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

const HeaderTop = ({ onOpenCategories }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const timeoutRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileTriggerRef = useRef(null);
  const ignoreOutsideRef = useRef(false);
  const prevCartCountRef = useRef(null);
  const cartBumpTimeoutRef = useRef(null);
  const [cartBump, setCartBump] = useState(false);

  const { user } = useSelector((state) => state.userAuth);
  const { items } = useSelector((state) => state.cart);
  const addNotice = useSelector((state) => state?.cart?.lastAddNotice);

  useEffect(() => {
    if (!addNotice) return;
    toast.info(addNotice);
    dispatch(clearAddNotice());
  }, [addNotice, dispatch]);

  const cartCount = Array.isArray(items)
    ? items.reduce((sum, it) => sum + Number(it?.quantity || 0), 0)
    : 0;
  const userName = String(user?.name || user?.username || "").trim();
  const userEmail = String(user?.email || "").trim();
  const avatarUrl = resolveAvatarUrl(user);
  const avatarInitial = getAvatarInitial(userName);

  useEffect(() => {
    const count = cartCount;
    const prev = prevCartCountRef.current;
    if (typeof prev === "number" && prev !== count) {
      setCartBump(true);
      clearTimeout(cartBumpTimeoutRef.current);
      cartBumpTimeoutRef.current = setTimeout(() => setCartBump(false), 420);
    }
    prevCartCountRef.current = count;
    return () => clearTimeout(cartBumpTimeoutRef.current);
  }, [cartCount]);

  useEffect(() => {
    const closeDrawer = () => setDrawerOpen(false);
    window.addEventListener("cherrfy:close-mobile-drawer", closeDrawer);
    return () => window.removeEventListener("cherrfy:close-mobile-drawer", closeDrawer);
  }, []);

  useEffect(() => {
    const openDrawer = () => {
      ignoreOutsideRef.current = true;
      setDrawerOpen(true);
      setTimeout(() => {
        ignoreOutsideRef.current = false;
      }, 0);
    };
    const toggleDrawer = () => {
      ignoreOutsideRef.current = true;
      setDrawerOpen((prev) => !prev);
      setTimeout(() => {
        ignoreOutsideRef.current = false;
      }, 0);
    };

    window.addEventListener("cherrfy:open-profile-drawer", openDrawer);
    window.addEventListener("cherrfy:toggle-profile-drawer", toggleDrawer);
    return () => {
      window.removeEventListener("cherrfy:open-profile-drawer", openDrawer);
      window.removeEventListener("cherrfy:toggle-profile-drawer", toggleDrawer);
    };
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onDocTap = (event) => {
      if (ignoreOutsideRef.current) return;
      const target = event.target;
      if (mobileMenuRef.current && mobileMenuRef.current.contains(target)) return;
      if (mobileTriggerRef.current && mobileTriggerRef.current.contains(target)) return;
      setDrawerOpen(false);
    };

    document.addEventListener("click", onDocTap);
    document.addEventListener("touchstart", onDocTap, { passive: true });
    return () => {
      document.removeEventListener("click", onDocTap);
      document.removeEventListener("touchstart", onDocTap);
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUserSession()).unwrap();
    } catch {
      // Continue client logout even if network/session revoke fails.
    }

    dispatch(logout());
    setLogoutConfirm(false);
    setDrawerOpen(false);
    setDropdownOpen(false);
    try {
      sessionStorage.removeItem("cherrfy_login_redirect");
    } catch {
      // ignore
    }
    navigate(USER_PATHS.LOGIN, { replace: true, state: { loggedOut: true } });
  };

  const toggleDropdown = (state) => {
    clearTimeout(timeoutRef.current);
    if (state) setDropdownOpen(true);
    else timeoutRef.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const navLinks = [
    { label: "My Orders", icon: "ClipboardList", path: USER_PATHS.ORDER },
    { label: "Message Center", icon: "MessageSquare", path: USER_PATHS.NOTIFICATIONS },
    { label: "Payment", icon: "CreditCard", path: USER_PATHS.PAYMENT },
    { label: "Wish List", icon: "Heart", path: USER_PATHS.WISHLIST_AND_FOLLOWED_STORES },
    { label: "My Coupons", icon: "TicketPercent", path: USER_PATHS.VAUCHERS },
  ];

  const getDisplayName = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "Account";
    return parts.slice(0, 2).join(" ");
  };

  return (
    <>
      <nav className="relative z-[100] border-b border-slate-200/70 bg-white" style={{ fontFamily: CORPORATE_FONT_FAMILY }}>
      <ConfirmDialog
        isOpen={logoutConfirm}
        onConfirm={handleLogout}
        onCancel={() => setLogoutConfirm(false)}
        title="Logout"
        message="Are you sure you want to exit?"
        confirmText="Logout"
        cancelText="Cancel"
        tone="danger"
      />

      <div className="mx-auto flex h-16 w-full max-w-[1680px] items-center gap-2 px-4 sm:gap-2.5 sm:px-5 lg:px-7 xl:gap-3.5 xl:px-12 2xl:px-[68px]">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={onOpenCategories}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200/70 bg-white text-slate-950 sm:h-11 sm:w-11 lg:hidden"
            aria-label="Open categories"
          >
            <Icons.Menu size={21} strokeWidth={2.4} />
          </button>
          <Link to="/" className="inline-flex shrink-0 items-center" aria-label="Cherrfy home">
            <span className="inline-flex h-[26px] w-[90px] items-center overflow-hidden sm:h-[29px] sm:w-[102px] lg:h-8 lg:w-[114px]">
              <img src={CHERRFY_LOGO_URL} className="h-full w-full object-cover object-center" alt="Cherrfy" />
            </span>
          </Link>
        </div>

        <div className="hidden min-w-[240px] flex-1 lg:flex lg:pl-3">
          <SearchBar className="max-w-[560px]" />
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1 md:gap-1.5">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("cherrfy:open-mobile-search"))}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200/70 bg-white text-slate-950 sm:h-11 sm:w-11 lg:hidden"
            aria-label="Open search"
          >
            <Icons.Search size={20} strokeWidth={2.5} />
          </button>

          <div className="flex items-center gap-3.5 md:gap-5 xl:gap-5">
            <div className="hidden items-center gap-5 md:flex">
              <AppDownloadMenu />
              <SettingsPopup hideTriggerOnMobile />
            </div>

            <div
              className="relative hidden lg:block"
              onMouseEnter={() => toggleDropdown(true)}
              onMouseLeave={() => toggleDropdown(false)}
            >
              <button
                type="button"
                onClick={() => setDropdownOpen((value) => !value)}
                className="group inline-flex h-11 items-center gap-1.5 px-1.5 text-[#20121A] transition-colors hover:text-[#B5163A]"
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full text-slate-950">
                  <Icons.UserRound size={25} strokeWidth={2.2} aria-hidden="true" />
                </div>
                <div className="hidden max-w-[120px] text-left leading-tight xl:block">
                  <p className="truncate text-xs font-normal text-slate-900">
                    {user ? `Hi, ${getDisplayName(userName)}` : "Sign in"}
                  </p>
                  <p className="text-[13px] font-medium text-slate-900">Account</p>
                </div>
              </button>

              <div
                className={[
                  "absolute right-0 top-full z-[130] mt-3 w-[204px] max-w-[calc(100vw-1rem)] rounded-2xl border-[0.5px] border-slate-200/70 bg-white p-3.5",
                  "origin-top-right transform-gpu transition-all duration-200 ease-out",
                  dropdownOpen
                    ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                    : "opacity-0 -translate-y-1 scale-[0.98] pointer-events-none",
                  ].join(" ")}
                role="menu"
                aria-hidden={!dropdownOpen}
                style={{ visibility: dropdownOpen ? "visible" : "hidden" }}
              >
                <div className="absolute -top-1.5 right-[52px] h-3 w-3 rotate-45 border-l border-t border-slate-200/70 bg-white" />
                <div className="relative">
                  {user ? (
                    <>
                      <div className="flex items-center gap-2.5">
                        <div className="h-[34px] w-[34px] shrink-0 overflow-hidden rounded-full bg-slate-200 text-slate-500">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={userName || "Profile"}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-sm font-medium">
                              {avatarInitial || <Icons.UserRound size={20} aria-hidden="true" />}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium leading-tight text-slate-900">
                            Welcome back, {getDisplayName(userName)}
                          </p>
                          <button
                            type="button"
                            onClick={() => setLogoutConfirm(true)}
                            className="mt-1 text-xs font-normal text-blue-600 hover:text-blue-700"
                          >
                            Sign Out
                          </button>
                        </div>
                      </div>

                      <div className="my-3 h-px bg-slate-200" />

                      <div className="space-y-1">
                        {navLinks.map((link) => {
                          const Icon = Icons[link.icon] || Icons.Circle;
                          return (
                            <Link
                              key={link.label}
                              to={link.path}
                              className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 text-[13px] font-normal text-[#59424D] transition-colors hover:bg-[#FFF8F6] hover:text-[#B5163A]"
                            >
                              <Icon size={14} strokeWidth={2} aria-hidden="true" />
                              {link.label}
                            </Link>
                          );
                        })}
                      </div>

                      <div className="my-3 h-px bg-slate-200" />

                      <Link
                        to={USER_PATHS.MANAGEPROFILE}
                        className="block rounded-lg px-1.5 py-1.5 text-[13px] font-normal text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                      >
                        Settings
                      </Link>

                      <button
                        type="button"
                        onClick={() => {
                          setDropdownOpen(false);
                          setLogoutConfirm(true);
                        }}
                        className="block w-full rounded-lg px-1.5 py-1.5 text-left text-[13px] font-medium text-[#D2042D] transition-colors hover:bg-[#FFF1F3] hover:text-[#B5163A]"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                          <Icons.UserRound size={18} aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium leading-tight text-slate-900">Welcome to Cherrfy</p>
                          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-500">Sign in for orders, wishlist, and checkout.</p>
                        </div>
                      </div>
                      <Link
                        to={USER_PATHS.LOGIN}
                        className="block w-full rounded-full bg-[#B5163A] px-3 py-2 text-center text-xs font-semibold text-white hover:bg-[#7F1028]"
                      >
                        Sign in / Register
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Link
              to={USER_PATHS.CART}
              className="relative inline-flex h-11 items-center gap-1 px-1.5 text-[#20121A] transition-colors hover:text-[#B5163A]"
              aria-label={`Cart with ${cartCount} item${cartCount === 1 ? "" : "s"}`}
            >
                <span className="relative">
                  <Icons.ShoppingCart size={26} strokeWidth={2.2} aria-hidden="true" />
                  {cartCount > 0 ? (
                    <span className={`absolute -right-2 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#B5163A] px-1 text-[10px] font-medium text-white ${cartBump ? "animate-pulse" : ""}`}>
                      {cartCount}
                    </span>
                  ) : null}
                </span>
              <span className="hidden text-left leading-tight xl:block">
                <span className="block text-[13px] font-medium">Cart</span>
              </span>
            </Link>
          </div>
        </div>

        {/* Mobile profile panel (full-screen) */}
        <div className="lg:hidden">
          {drawerOpen
            ? (typeof document !== "undefined"
              ? createPortal(
                <div
                  className={[
                    "fixed inset-0 z-[110]",
                    "transform-gpu transition-all duration-200 ease-out",
                    drawerOpen
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 translate-y-2 pointer-events-none",
                  ].join(" ")}
                  role="menu"
                >
                  <div
                    className="absolute inset-0 bg-black/10 backdrop-blur-sm"
                    onClick={() => setDrawerOpen(false)}
                  />
                  <div
                    ref={mobileMenuRef}
                    className="absolute inset-0 bg-white flex flex-col"
                  >
                    <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
                      <div>
                        <p className="text-sm font-medium text-slate-900 truncate">{userName || "Guest"}</p>
                        <p className="text-xs text-slate-500 truncate">{userEmail || "Welcome to Cherrfy"}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDrawerOpen(false)}
                        className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200"
                        aria-label="Close profile"
                      >
                        <Icons.X size={18} />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-2">
                      {user ? (
                        <>
                          {navLinks.map((link) => {
                            const Icon = Icons[link.icon] || Icons.Circle;
                            return (
                              <Link
                                key={link.label}
                                to={link.path}
                                className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-normal text-slate-900"
                                onClick={() => setDrawerOpen(false)}
                              >
                                <Icon size={18} aria-hidden="true" />
                                {link.label}
                              </Link>
                            );
                          })}
                          <button
                            onClick={() => setLogoutConfirm(true)}
                            className="w-full flex items-center gap-3 rounded-2xl border border-rose-200 px-4 py-3 text-sm font-normal text-rose-600"
                          >
                            <Icons.LogOut size={18} /> Logout
                          </button>
                        </>
                      ) : (
                        <Link
                          to={USER_PATHS.LOGIN}
                          onClick={() => setDrawerOpen(false)}
                          className="block w-full text-center rounded-2xl bg-[#B5163A] px-4 py-3 text-sm font-semibold text-white"
                        >
                          Sign in / Register
                        </Link>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )
              : null)
            : null}
        </div>
      </div>

      {/* Mobile Search Row (hidden; using bottom menu search) */}
      <div className="hidden">
        <SearchBar />
      </div>
      </nav>
      <EmailVerifyBanner />
    </>
  );
};

export default HeaderTop;
